import React from 'react'
import classNames from 'classnames'
import get from 'lodash-es/get'

import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles'

import isClassicGame from 'lib/isClassicGame'
import { IGame, GameType, MatchStatus, IArchiveGame } from 'protos'

import GameCardTeam from './components/GameCardTeam'
import GameCardButton from './components/GameCardButton'
import GameCardHeader from './components/GameCardHeader'
import GameCardScoresLive from './components/GameCardScoresLive'
import GameCardScoresInvite from './components/GameCardScoresInvite'
import GameCardScoresDefault from './components/GameCardScoresDefault'
import GameCardScoresCompleted from './components/GameCardScoresCompleted'
import GameCardContentInvite from './components/GameCardContentInvite'
import GameCardContentCompleted from './components/GameCardContentCompleted'
import GameCardBottomInfoBarLive, { GameCardClassicBottomInfoBarLive } from './components/GameCardBottomInfoBarLive'
import GameCardBottomInfoBarInvite from './components/GameCardBottomInfoBarInvite'
import GameCardBottomInfoBarDefault from './components/GameCardBottomInfoBarDefault'
import GameCardProgressBar, { GameCardClassicProgressBar } from './components/GameCardProgressBar'

const styles = createStyles({
  root: {
    filter: 'drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1))',
  },
})

interface Props {
  game: IGame | IArchiveGame
  className?: string
  displayQuizButton?: boolean
  onCickDisplayQuizButton?: () => void
}

type InjectedProps = Props & WithStyles<typeof styles>

const getFormattedStartTime = (game: IGame) => {
  const date = new Date(Number(game.startTime) * 1000)
  const hours = date.getHours()
  const minutes = date.getMinutes()

  return `${hours < 10 ? `0${hours}` : hours}:${minutes < 10 ? `0${minutes}` : minutes}`
}

const isLive = (game: IGame) => (game.match?.status ?? game.classicGameInfo?.status) === MatchStatus.MS_LIVE
const isFinished = (game: IGame) => (game.match?.status ?? game.classicGameInfo?.status) === MatchStatus.MS_FINISHED
const shouldShowMatchScore = (game: IGame) => !!game.match?.currentScore
const shouldShowHeaderAvatar = (game: IGame) => game.type === GameType.GT_PRIVATE && !game.invitation
const shouldShowInvitationInfo = (game: IGame) => {
  return game.type === GameType.GT_PRIVATE
    && !!game.ownEntry?.host
    && Number(game.invitedCount) > Number(game.acceptedCount)
}
const getFormattedMatchScore = (game: IGame) => game.match?.currentScore?.replace('-', ':') ?? '0 : 0'
const isActiveGame = (game: IGame | IArchiveGame): game is IGame => {
  return Object.getOwnPropertyNames(game).some((field) => field === 'gameId')
}
const isArchiveGame = (game: IGame | IArchiveGame): game is IArchiveGame => {
  return Object.getOwnPropertyNames(game).some((field) => field === 'game')
}

const renderHeader = (
  game: IGame,
  displayQuizButton?: Props['displayQuizButton'],
  onClickDisplayQuizButton?: Props['onCickDisplayQuizButton'],
) => {
  if (isClassicGame(game)) {
    return (
      <GameCardHeader
        isClassicGame
        homeTeam={game.classicGameInfo?.competition?.name}
        separator="-"
        awayTeam={game.classicGameInfo?.round?.name}
      />
    )
  }

  return (
    <GameCardHeader
      homeTeam={game.match?.homeTeam?.name}
      awayTeam={game.match?.awayTeam?.name}
      separator={shouldShowMatchScore(game) ? getFormattedMatchScore(game) : getFormattedStartTime(game)}
      avatar={shouldShowHeaderAvatar(game) ? {
        url: get(game, 'game.progressInviteFrom.photoUrl', get(game, 'host.photoUrl')),
        value: game.acceptedCount!,
      } : undefined}
      displayQuizButton={displayQuizButton}
      onClickDisplayQuizButton={onClickDisplayQuizButton}
    />
  )
}

const renderIncomingInvitation = (game: IGame) => {
  // TODO: firstPrize currently has fake value (max pot)
  return (
    <>
      <GameCardContentInvite
        name={get(game, 'game.progressInviteFrom.name', game.host!.name!)}
        photoUrl={get(game, 'game.progressInviteFrom.photoUrl', game.host!.photoUrl!)}
        entryFee={+game.entryFee!.value!}
      />
      <GameCardScoresInvite
        invited={game.invitedCount!}
        accepted={game.acceptedCount!}
        firstPrize={+game.maxPot!.value!}
        remainingSec={game.match!.kickoffRemainingSec!}
      />
      <GameCardButton to={`/games/${game.gameId}/invitation`} />
    </>
  )
}

const renderOutgoingInvitation = (game: IGame) => {
  return (
    <>
      <GameCardTeam
        team={game.ownEntry!.team!}
      />
      <GameCardScoresDefault
        entryFee={+game.entryFee!.value!}
        firstPrize={+game.maxPot!.value!}
        remainingSec={game.match!.kickoffRemainingSec!}
      />
      <GameCardProgressBar
        variant="invite"
        value={+game.acceptedCount!}
        max={+game.invitedCount!}
      />
      <GameCardBottomInfoBarInvite
        invited={+game.invitedCount!}
        accepted={+game.acceptedCount!}
      />
    </>
  )
}

const renderLiveOrFinished = (game: IGame) => {
  return (
    <>
      <GameCardTeam
        team={game.ownEntry!.team!}
      />
      <GameCardScoresLive
        rank={+game.ownEntry!.rank!}
        score={+game.ownEntry!.score!}
        firstPrize={+game.maxPot!.value!}
      />
      {
        isClassicGame(game)
          ? (
              <>
                <GameCardClassicProgressBar
                  max={game.progressClassic?.matchCount ?? 1}
                  values={game.progressClassic!.progressPart!}
                />
                {
                  isLive(game)
                    ? <GameCardClassicBottomInfoBarLive progress={game.progressClassic} />
                    : <GameCardBottomInfoBarDefault status={game.classicGameInfo?.status ?? MatchStatus.MS_FINISHED} />
                }
              </>
            )
          : (
              <>
                <GameCardProgressBar max={1} variant="match" value={game.progress!} />
                {
                  isLive(game)
                    ? <GameCardBottomInfoBarLive clock={game.match!.clock} period={game.match!.period} />
                    : <GameCardBottomInfoBarDefault status={game.match?.status ?? MatchStatus.MS_FINISHED} />
                }
              </>
            )
      }
    </>
  )
}

const renderDefaultActiveGameCard = (game: IGame) => {
  return (
    <>
      <GameCardTeam
        team={game.ownEntry!.team!}
      />
      <GameCardScoresDefault
        entryFee={+game.entryFee!.value!}
        firstPrize={+game.maxPot!.value!}
        remainingSec={Math.max(0, Number(game.startTime) - Math.round(Date.now()/1000))}
      />
      <GameCardProgressBar
        variant="match"
        value={0}
        max={1}
      />
      <GameCardBottomInfoBarDefault
        status={game.match?.status! ?? game.classicGameInfo?.status!}
      />
    </>
  )
}

const renderActiveGameCard = (game: IGame) => {
  if (game.invitation) {
    // TODO: firstPrize currently has fake value (max pot)
    return renderIncomingInvitation(game)
  }

  if (isLive(game) || isFinished(game)) {
    return renderLiveOrFinished(game)
  }

  if (shouldShowInvitationInfo(game)) {
    return renderOutgoingInvitation(game)
  }

  return renderDefaultActiveGameCard(game)
}

const renderArchiveGameCard = (game: IArchiveGame) => {
  const rank = +game!.result!.rank!
  const winnings = get(game, 'result.prize.value', -game.game!.entryFee!)
  const bestOpponentRaw = rank === 1
    ? game.top!.find((entry) => entry.rank === 2)
    : game.top!.find((entry) => entry.rank === 1)

  const bestOpponent = {
    rank: get(bestOpponentRaw, 'rank', 0) as number,
    points: get(bestOpponentRaw, 'score', 0) as number,
    photoUrl: get(bestOpponentRaw, 'user.photoUrl', ''),
  }

  return (
    <>
      <GameCardContentCompleted
        rank={rank}
        winnings={winnings}
        bestOpponent={bestOpponent}
      />
      <GameCardScoresCompleted
        size={+game.game!.acceptedCount! || +game.game!.size!}
        score={+game.result!.score!}
        winnings={winnings}
      />
    </>
  )
}

const GameCard: React.SFC<InjectedProps> = ({
  game,
  classes,
  className,
  displayQuizButton,
  onCickDisplayQuizButton,
}) => {
  if (isActiveGame(game)) {
    return (
      <div className={classNames(className, classes.root)}>
        {renderHeader(game, displayQuizButton, onCickDisplayQuizButton)}
        {renderActiveGameCard(game)}
      </div>
    )
  }

  if (isArchiveGame(game)) {
    const { game: _game } = game

    return (
      <div className={classNames(className, classes.root)}>
        {renderHeader(_game!, displayQuizButton, onCickDisplayQuizButton)}
        {renderArchiveGameCard(game)}
      </div>
    )
  }

  return null
}

export default withStyles(styles)(GameCard)
